<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { mcqChapterClusters } from "~/features/computer/data/mcq-clusters";

const quickLinks = [
  {
    title: "MCQs Top 100",
    to: "/11th/computer/mcqs/top-100/",
    description: "Primary traffic page jahan se objective prep ka best flow start hota hai.",
  },
  {
    title: "Practice Mode MCQs",
    to: "/11th/computer/practice/mcqs/",
    description: "Mixed syllabus MCQs ko filters aur random mode ke sath practice karo.",
  },
];

const supportLinks = [
  { title: "Pairing Scheme", to: "/11th/computer/pairing-scheme/", description: "Dekhein kis chapter se kya aata hai." },
  { title: "Paper Pattern", to: "/11th/computer/paper-pattern/", description: "Samjhein objective part paper me kaise aata hai." },
  { title: "Important Questions", to: "/11th/computer/important-questions/", description: "Objective ke sath important descriptive prep bhi dekhein." },
];

const faqs = [
  {
    question: "11th class computer ke MCQs kis chapter se aate hain?",
    answer:
      "Computer ke MCQs tamam chapters se aate hain, is liye objective prep me poori book ka overview zaroori hota hai.",
  },
  {
    question: "1st year computer MCQs ke liye sab se important chapter kaun sa hai?",
    answer:
      "Chapter 2 Python sab se important hota hai, us ke baad Chapter 1 SDLC, Chapter 3 Algorithms aur Chapter 4 Data Structures strong MCQ coverage dete hain.",
  },
  {
    question: "Top 100 MCQs aur chapter-wise MCQs me kya difference hai?",
    answer:
      "Top 100 MCQs fast revision ke liye shortlist hota hai, jab ke chapter-wise MCQs har chapter ko detail me cover karte hain.",
  },
];

usePageSeo({
  title: "11th Class Computer MCQs | Chapter-wise Hub",
  description: "11th class computer MCQs ke liye top 100 page aur chapter-wise MCQ clusters ek jagah.",
  keywords: "11th class computer mcqs, computer chapter wise mcqs, first year computer mcqs top 100",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "MCQs", item: "/11th/computer/mcqs/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer MCQ Hub",
    items: [
      { name: "Top 100 MCQs", path: "/11th/computer/mcqs/top-100/" },
      ...mcqChapterClusters.map((item) => ({
        name: `Chapter ${item.chapter} ${item.shortTitle} MCQs`,
        path: item.clusterTo,
      })),
      { name: "Pairing Scheme", path: "/11th/computer/pairing-scheme/" },
      { name: "Paper Pattern", path: "/11th/computer/paper-pattern/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">MCQ Hub</p>
      <h1 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">
        11th Class Computer MCQs
      </h1>
      <p class="mt-4 max-w-3xl text-sm leading-7 text-slate-700 dark:text-slate-300">
        Agar objective prep start karni hai to pehle top-100 page kholo. Us ke baad chapter-wise MCQ clusters se specific chapter ko target karo.
      </p>
      <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
        11th class computer paper me MCQs tamam chapters se aate hain, lekin Python, SDLC aur Algorithms se shuru karna zyada practical route hota hai.
      </p>
    </section>

    <section class="grid gap-4 md:grid-cols-2">
      <NuxtLink
        v-for="item in quickLinks"
        :key="item.to"
        :to="item.to"
        class="rounded-[24px] border border-slate-200 bg-white p-5 transition hover:border-sky-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-sky-700 dark:hover:bg-slate-950"
      >
        <h2 class="text-xl font-semibold text-slate-950 dark:text-white">{{ item.title }}</h2>
        <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
      </NuxtLink>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Chapter Clusters</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">All 9 chapter pages</h2>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
        Agar aap chapter wise MCQs practice karna chahte hain to neeche se direct chapter choose karein.
      </p>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <NuxtLink
          v-for="item in mcqChapterClusters"
          :key="item.slug"
          :to="item.clusterTo"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Chapter {{ item.chapter }}</p>
          <h3 class="mt-2 text-lg font-semibold text-slate-950 dark:text-white">{{ item.shortTitle }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related Prep</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Use MCQs with pattern and pairing</h2>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
        Objective prep tab zyada strong hoti hai jab aap paper pattern aur pairing scheme ko sath samajh kar MCQs practice karte hain.
      </p>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in supportLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">11th Computer MCQs FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>
  </PageContainer>
</template>
